/**
 * LandingNav
 *
 * Fixed navigation bar that overlays the hero animation.
 * Starts transparent; transitions to cream + shadow once the user scrolls
 * past the 80px threshold (well before the hero exits viewport).
 *
 * Desktop: logo left | center links | right CTAs
 * Mobile:  logo left | hamburger right → slide-down menu
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function LandingNav() {
  const { user, loading: authLoading } = useAuth();
  const [scrolled,   setScrolled  ] = useState(false);
  const [menuOpen,   setMenuOpen  ] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change / scroll
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  function scrollTo(id: string) {
    setMenuOpen(false);
    if (window.location.pathname === '/') {
      // Already on the home page — smooth-scroll directly
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 80);
    } else {
      // On an interior page — navigate to home with hash; browser handles scroll
      window.location.href = `/#${id}`;
    }
  }

  return (
    <>
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen
            ? 'bg-cream/95 backdrop-blur-sm shadow-warm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">

          {/* Logo — M+S monogram + wordmark text */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={() => setMenuOpen(false)}>
            <img
              src="/logo-icon.png"
              alt="MomentSent"
              className="h-9 w-auto object-contain"
            />
            <span className="font-heading text-lg font-semibold text-navy tracking-tight">
              MomentSent
            </span>
          </Link>

          {/* Center links — desktop only */}
          <div className="hidden md:flex items-center gap-8">
            <NavLink onClick={() => scrollTo('how-it-works')}>How it works</NavLink>
            <NavLink onClick={() => scrollTo('pricing-teaser')}>Pricing</NavLink>
            <NavLink onClick={() => scrollTo('signup')}>Join the list</NavLink>
            <Link
              to="/contact"
              className="font-body text-sm text-gray-500 hover:text-navy transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/about"
              className="font-body text-sm text-gray-500 hover:text-navy transition-colors"
            >
              About
            </Link>
          </div>

          {/* Right CTAs — desktop */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            {!authLoading && (user ? (
              <Link
                to="/dashboard"
                className="btn-primary text-sm px-5 py-2"
                style={{ minHeight: 36 }}
              >
                Dashboard →
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="font-body text-sm text-gray-500 hover:text-navy transition-colors"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className="btn-primary text-sm px-5 py-2 inline-flex items-center justify-center"
                  style={{ minHeight: 36 }}
                >
                  Sign Up Now
                </Link>
              </>
            ))}
          </div>

          {/* Mobile right: Log in / Dashboard link + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            {!authLoading && (user ? (
              <Link
                to="/dashboard"
                className="font-body text-sm font-medium text-navy hover:text-sage transition-colors px-3 py-1.5"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="font-body text-sm font-medium text-navy hover:text-sage transition-colors px-3 py-1.5"
              >
                Log in
              </Link>
            ))}
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center justify-center w-10 h-10 rounded-xl text-navy hover:bg-navy/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

        </div>
      </nav>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 md:hidden"
            onClick={() => setMenuOpen(false)}
          />

          {/* Slide-down drawer — navy so it stands out from cream bg */}
          <div className="fixed top-16 left-0 right-0 z-40 md:hidden bg-navy shadow-2xl">
            <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col gap-1">

              {/* Nav items */}
              <MobileNavItem onClick={() => scrollTo('how-it-works')}>How it works</MobileNavItem>
              <MobileNavItem onClick={() => scrollTo('pricing-teaser')}>Pricing</MobileNavItem>
              <MobileNavLink to="/about"   onClick={() => setMenuOpen(false)}>About</MobileNavLink>
              <MobileNavLink to="/contact" onClick={() => setMenuOpen(false)}>Contact</MobileNavLink>

              {/* Divider */}
              <div className="border-t border-white/10 my-3" />

              {/* CTA */}
              <Link
                to="/signup"
                onClick={() => setMenuOpen(false)}
                className="block w-full py-3 px-6 rounded-full bg-sage text-navy font-body font-semibold text-sm hover:bg-sage/90 transition-colors text-center"
              >
                Sign Up Now
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function NavLink({
  onClick, children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="font-body text-sm text-gray-500 hover:text-navy transition-colors"
    >
      {children}
    </button>
  );
}

function MobileNavItem({
  onClick, children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left font-body text-base font-medium text-white/90 hover:text-sage transition-colors px-2 py-2.5 rounded-lg hover:bg-white/5"
    >
      {children}
    </button>
  );
}

function MobileNavLink({
  to, onClick, children,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="font-body text-base font-medium text-white/90 hover:text-sage transition-colors px-2 py-2.5 rounded-lg hover:bg-white/5 block"
    >
      {children}
    </Link>
  );
}
